import { JSX, useEffect } from "react";
import { UpdateUser, User } from "common";
import { Dialog } from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useForm } from "react-hook-form";
import { useUpdateUserByUserId } from "@via/pages/account/admin/admin.api.tsx";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { requiredValidation } from "@via/utils/form-validators.tsx";
import { removeFalsyValues } from "@via/utils/helpers.tsx";
import SelectComponent from "@via/components/select/select.tsx";
import { USER_ROLE_OPTIONS } from "@via/utils/constants.tsx";

export default function EditUserForm({
  setOpen,
  user,
}: {
  setOpen: (open: boolean) => void;
  user: User | null;
}): JSX.Element {
  const {
    mutate: updateUser,
    isPending: isUpdateUserLoading,
    isSuccess: isUpdateUserSuccess,
  } = useUpdateUserByUserId();

  const { control, handleSubmit } = useForm<UpdateUser>({
    mode: "onChange",
    defaultValues: {
      username: user?.userName,
      name: user?.name,
      email: user?.email,
      institute: user?.institute || "",
      lab: user?.lab || "",
      role: user?.role || undefined,
    },
  });

  const handleUpdateUser = (payload: UpdateUser) => {
    if (user?.id) {
      const updateUserPayload = {
        userId: user.id,
        params: removeFalsyValues(payload),
      };
      updateUser(updateUserPayload);
    }
  };

  useEffect(() => {
    isUpdateUserSuccess && handleClose();
  }, [isUpdateUserSuccess]);

  const handleClose = () => setOpen(false);
  const onSubmit = handleSubmit(handleUpdateUser);

  return (
    <form
      className="flex h-full flex-col divide-y divide-gray-200 bg-white shadow-xl"
      onSubmit={e => {
        e.preventDefault();
        onSubmit(e);
      }}>
      <div className="h-0 flex-1 overflow-y-auto">
        <div className="border-b bg-gray-50 px-4 py-6 sm:px-6">
          <div className="flex items-center justify-between">
            <Dialog.Title className="text-base font-semibold leading-6 text-gray-900">
              User
            </Dialog.Title>
            <div className="ml-3 flex h-7 items-center">
              <button
                type="button"
                className="relative rounded-md text-gray-900 duration-150 ease-in-out hover:text-gray-600 focus:outline-none"
                onClick={handleClose}>
                <span className="absolute -inset-2.5" />
                <span className="sr-only">Close panel</span>
                <XMarkIcon className="h-6 w-6" aria-hidden="true" />
              </button>
            </div>
          </div>
          <div className="mt-1">
            <p className="text-sm text-gray-500">Update user details.</p>
          </div>
        </div>
        <div className="flex flex-1 flex-col justify-between">
          <div className="divide-y divide-gray-200 px-4 sm:px-6">
            <div className="space-y-6 pb-5 pt-6">
              <FormInputComponent
                control={control}
                name="username"
                label="Username"
                placeholder="Enter Username"
                validations={requiredValidation("Username")}
                showRequiredField
              />
              <FormInputComponent
                control={control}
                name="name"
                label="Name"
                placeholder="Enter name"
                validations={requiredValidation("Name")}
                showRequiredField
              />
              <FormInputComponent
                control={control}
                name="email"
                label="Email"
                placeholder="Enter email"
                validations={requiredValidation("Email")}
                showRequiredField
              />
              <FormInputComponent
                control={control}
                name="institute"
                label="Institute"
                placeholder="Enter institute"
              />
              <FormInputComponent
                control={control}
                name="lab"
                label="Lab"
                placeholder="Enter lab"
              />
              <div>
                <label className="mb-2 block text-sm font-medium leading-6 text-gray-900">
                  Role
                </label>
                <SelectComponent
                  control={control}
                  name="role"
                  placeholder="Choose User Role"
                  options={USER_ROLE_OPTIONS}
                  enableClear={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 justify-end px-4 py-4">
        <button
          id="edit-user-details-cancel"
          type="button"
          className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
          onClick={handleClose}>
          Cancel
        </button>
        <button
          id="edit-user-details-save"
          type="submit"
          className="ml-4 inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isUpdateUserLoading}>Save</Spinner>
        </button>
      </div>
    </form>
  );
}
